import React, { useEffect, useState } from 'react';
import {
  FaUser,
  FaMapMarkerAlt,
  FaBookOpen,
  FaBriefcase,
  FaInfoCircle,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import {
  useNavigate,
  useLocation,
  Outlet
} from 'react-router-dom';

import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsAsRead,
} from '../../../services/candidate/api';

import TopNavbar from '../../top-navbar/TopNav';
import '../../top-navbar/topnavBar.css';
import './CandidateSidebar.css';
import CandidateNotification from '../../Notification/CandidateNotification';
import { useCandidateStatus } from '../CandidateStatusContext';

const requestTypeByPath = {
  'personal-info': 'PERSONAL_DETAILS',
  'address-info': 'ADDRESS_DETAILS',
  'education-info': 'EDUCATION_DETAILS',
  'employment-info': 'EMPLOYMENT_DETAILS',
};

const navItems = [
  {
    path: 'dashboard',
    label: 'Instructions',
    icon: <FaInfoCircle />,
  },
  {
    path: 'personal-info',
    label: 'Personal Details',
    icon: <FaUser />,
    requestType: 'PERSONAL_DETAILS',
  },
  {
    path: 'address-info',
    label: 'Address Details',
    icon: <FaMapMarkerAlt />,
    requestType: 'ADDRESS_DETAILS',
  },
  {
    path: 'education-info',
    label: 'Educational Details',
    icon: <FaBookOpen />,
    requestType: 'EDUCATION_DETAILS',
  },
  {
    path: 'employment-info',
    label: 'Employment Details',
    icon: <FaBriefcase />,
    requestType: 'EMPLOYMENT_DETAILS',
  },
];

const CandidateSidebar = ({ isSidebarDisabled }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    loading,
    error,
    onboardingCompleted,
    latestRequest,
    hasActiveEditCycle,
  } = useCandidateStatus();

  const [userEmail, setUserEmail] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [messages, setMessages] = useState([]);

  // Not security relevant, so it stays in localStorage
  const [isInstructionsCompleted, setIsInstructionsCompleted] = useState(
    localStorage.getItem('candidateInstructionsCompleted') === 'true'
  );

  useEffect(() => {
    setIsInstructionsCompleted(
      localStorage.getItem('candidateInstructionsCompleted') === 'true'
    );
  }, [location.pathname]);

  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (email) {
      setUserEmail(email);
    }
  }, []);

  // Until the backend status is known, keep sections closed
  const statusUnknown = loading || error || onboardingCompleted === null;

  const isSectionClickable = (path) => {
    if (isSidebarDisabled) return false;

    const requestType = requestTypeByPath[path];
    if (!requestType) return false;

    if (statusUnknown) return false;

    // Onboarding not finished: normal onboarding, sections are open
    if (!onboardingCompleted) return true;

    // Onboarding finished: only an approved edit request opens a section
    return latestRequest(requestType)?.status === 'APPROVED';
  };

  const isDeclarationClickable = () => {
    if (isSidebarDisabled || statusUnknown) return false;
    if (!onboardingCompleted) return true;
    return !hasActiveEditCycle;
  };

  const isItemDisabled = (item) => {
    if (isSidebarDisabled) return true;

    if (item.path === 'dashboard') {
      return isInstructionsCompleted || onboardingCompleted === true;
    }

    if (item.requestType) {
      return !isSectionClickable(item.path);
    }

    return false;
  };

  const handleNavigation = (path, event) => {
    event.preventDefault();

    if (isSidebarDisabled) return;

    if (path === 'dashboard') {
      if (isInstructionsCompleted || onboardingCompleted) return;
      navigate(`/candidate/${path}`);
      return;
    }

    if (path === 'candidate-dashboard') {
      navigate(`/candidate/${path}`);
      return;
    }

    if (path === 'declaration') {
      if (isDeclarationClickable()) {
        navigate(`/candidate/${path}`);
      }
      return;
    }

    if (!isSectionClickable(path)) return;

    navigate(`/candidate/${path}`);
  };

  const handleIncomingNotification = (notification) => {
    setMessages((prev) => [
      {
        message: notification.message,
        notificationTime:
          notification.notificationTime || notification.createdAt,
        ...notification,
      },
      ...prev,
    ]);
  };

  const sidebarWidth = '12rem';

  return (
    <div className="candidate-dashboard-container">

      <CandidateNotification onNotification={handleIncomingNotification} />

      <TopNavbar
        showNotification={true}
        notificationConfig={{
          fetchNotificationsApi: fetchNotifications,
          fetchUnreadCountApi: fetchUnreadCount,
          markAllReadApi: markAllNotificationsAsRead,
        }}
      />

      <button
        className="hamburger-btn"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <FaTimes /> : <FaBars />}
      </button>

      <aside
        className={`candidate-dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}
        style={{ width: sidebarWidth }}
      >

        <button
          className="sidebar-close-btn"
          onClick={() => setIsSidebarOpen(false)}
        >
          <FaTimes />
        </button>

        <nav>
          <ul className="candidate-sidebar-menu">

            {navItems.map((item, index) => {
              const isActive = location.pathname.includes(item.path);
              const disabled = isItemDisabled(item);

              return (
                <React.Fragment key={item.path}>

                  <li
                    className={`
                      candidate-sidebar-menu-item
                      ${disabled ? 'disabled' : ''}
                      ${isActive ? 'active' : ''}
                    `}
                    onClick={(e) => {
                      if (disabled) {
                        e.preventDefault();
                        return;
                      }
                      handleNavigation(item.path, e);
                    }}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}
                    title={
                      disabled && onboardingCompleted && hasActiveEditCycle
                        ? 'This section is not currently approved for editing'
                        : ''
                    }
                  >
                    <span className="candidate-sidebar-icon">
                      {item.icon}
                    </span>

                    <span>{item.label}</span>
                  </li>

                  {index < navItems.length - 1 && (
                    <div className="candidate-sidebar-divider"></div>
                  )}

                </React.Fragment>
              );
            })}

          </ul>
        </nav>
      </aside>

      <main className="candidate-dashboard-main">

        <div className="candidate-dashboard-header-row">
          <div className="candidate-dashboard-title-bar"></div>
        </div>

        <div className="candidate-dashboard-page-content">
          <Outlet />
        </div>

      </main>

    </div>
  );
};

export default CandidateSidebar;